import React, { useState } from 'react';
import { Eye, EyeOff, Lock, User, ArrowRight, Shield, Sparkles, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { api } from '../utils/apiClient';
import { LegalplusLogo, LegalplusMark } from './brand/LegalplusLogo';
import LegalplusGlobe from './brand/LegalplusGlobe';

const TRUST_BADGES = [
  { k: '99.9%',   l: 'Uptime SLA' },
  { k: 'AES-256', l: 'Encriptación' },
  { k: '24/7',    l: 'Auditoría' },
];

const Login = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!username || !password) {
      setError('Ingrese su usuario y contraseña.');
      return;
    }
    setLoading(true);
    try {
      const response = await api.post('/login', { identificador: username, password });
      onLogin(response.user);
      navigate('/');
    } catch (err) {
      setError(err.message || 'Credenciales incorrectas. Verifique e intente de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-brand-night text-brand-silver font-sans overflow-hidden">

      {/* ═══════════════════════════════════════════════════════════════
          PANEL IZQUIERDO — Hero editorial
          ═══════════════════════════════════════════════════════════════ */}
      <div className="hidden lg:flex lg:w-[55%] xl:w-[58%] relative">
        {/* Fondo cinematográfico */}
        <div className="absolute inset-0 bg-brand-radial-hero" />

        {/* Grid blueprint animado — motion respeta reduced */}
        <div
          className="absolute inset-0 bg-grid-blueprint animate-grid-drift opacity-50"
          aria-hidden="true"
        />

        {/* Globe wireframe 3D — red jurídica conectada */}
        <div
          className="absolute top-1/2 left-[58%] -translate-x-1/2 -translate-y-[48%] pointer-events-none select-none opacity-90 mix-blend-screen"
          aria-hidden="true"
        >
          <LegalplusGlobe size={620} density={22} />
        </div>

        {/* Viñeta lateral derecha (integración con panel formulario) */}
        <div
          className="absolute top-0 right-0 h-full w-32 pointer-events-none"
          style={{ background: 'linear-gradient(90deg, transparent 0%, #050B18 100%)' }}
        />

        {/* Hairline superior */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-indigo/50 to-transparent" />

        {/* CONTENIDO HERO */}
        <div className="relative z-10 flex flex-col justify-between p-14 xl:p-20 w-full">

          {/* ── Top: logo oficial + sistema label ── */}
          <div className="flex items-start justify-between">
            <div className="relative inline-flex">
              {/* Halo breathing detrás del logo real */}
              <div
                className="absolute inset-0 -m-6 rounded-full bg-brand-indigo/25 blur-3xl animate-halo-breathe pointer-events-none"
                aria-hidden="true"
              />
              <LegalplusLogo variant="full" tone="light" size="lg" className="relative" />
            </div>

            <div className="hidden xl:flex items-center gap-2 text-[10px] font-semibold tracking-[0.22em] text-brand-silver-mute uppercase">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-cyan animate-pulse" />
              Sistema operativo
            </div>
          </div>

          {/* ── Centro: copy editorial ── */}
          <div className="max-w-xl">
            <div className="flex items-center gap-3 mb-8">
              <Sparkles size={14} className="text-brand-amber" />
              <span className="eyebrow text-brand-amber">Intelligence for legal operations</span>
            </div>

            <h1 className="font-display font-bold text-5xl xl:text-6xl leading-[0.98] tracking-[-0.03em] text-brand-silver">
              El sistema nervioso
              <br />
              de su práctica
              <span className="relative inline-block ml-2">
                <span className="bg-gradient-to-r from-brand-indigo-soft via-brand-indigo to-brand-cyan bg-clip-text text-transparent">
                  legal.
                </span>
                <span className="absolute -bottom-2 left-0 right-0 h-px bg-gradient-to-r from-brand-indigo/70 to-transparent" />
              </span>
            </h1>

            <p className="mt-8 text-[17px] leading-[1.65] text-brand-silver-dim max-w-lg">
              Casos, facturación, seguimiento judicial y datos en tiempo real — orquestados
              para abogados, asistentes y contabilidad. Diseñado para firmas que operan con
              aseguradoras en Colombia.
            </p>

            {/* Trust badges */}
            <div className="mt-10 grid grid-cols-3 gap-3 max-w-md">
              {TRUST_BADGES.map((b, i) => (
                <div
                  key={b.k}
                  className="group relative rounded-brand border border-white/[0.06] bg-white/[0.02] px-4 py-3.5 backdrop-blur-sm transition-all duration-[400ms] ease-brand hover:border-brand-indigo/30 hover:bg-white/[0.04]"
                  style={{ animationDelay: `${i * 80}ms` }}
                >
                  <p className="font-mono text-[15px] font-semibold text-brand-silver tabular-nums">{b.k}</p>
                  <p className="mt-1 text-[10px] tracking-[0.22em] font-semibold text-brand-silver-mute uppercase">{b.l}</p>
                  <div className="absolute left-0 top-0 h-full w-px bg-gradient-to-b from-brand-indigo/0 via-brand-indigo/40 to-brand-indigo/0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              ))}
            </div>
          </div>

          {/* ── Bottom: footer editorial ── */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5 text-[10px] tracking-[0.22em] font-semibold text-brand-silver-mute uppercase">
              <Shield size={13} className="text-brand-indigo" />
              <span>Acceso controlado por roles · Auditoría inmutable</span>
            </div>
            <div className="hidden xl:flex items-center gap-1.5 text-[10px] tracking-[0.22em] font-semibold text-brand-silver-mute/70 uppercase">
              <span>Bogotá</span>
              <span className="text-brand-indigo/50">·</span>
              <span>Colombia</span>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          PANEL DERECHO — Formulario
          ═══════════════════════════════════════════════════════════════ */}
      <div className="w-full lg:w-[45%] xl:w-[42%] flex items-center justify-center p-6 md:p-12 relative bg-brand-night">
        {/* Gradiente sutil lateral */}
        <div className="absolute inset-y-0 left-0 w-px bg-gradient-to-b from-transparent via-brand-indigo/20 to-transparent" />
        {/* Halo radial centrado */}
        <div className="absolute inset-0 pointer-events-none" style={{
          background: 'radial-gradient(ellipse 500px 400px at 50% 50%, rgba(79,125,255,0.06) 0%, transparent 70%)'
        }} />

        <div className="w-full max-w-[420px] relative">

          {/* ── Logo mobile ── */}
          <div className="lg:hidden mb-10 flex justify-center">
            <div className="relative inline-flex">
              <div
                className="absolute inset-0 -m-6 rounded-full bg-brand-indigo/25 blur-3xl animate-halo-breathe pointer-events-none"
                aria-hidden="true"
              />
              <LegalplusLogo variant="full" tone="light" size="md" className="relative" />
            </div>
          </div>

          {/* ── Eyebrow ── */}
          <div className="flex items-center gap-2 mb-5">
            <span className="inline-block w-8 h-px bg-brand-indigo" />
            <span className="eyebrow text-brand-indigo">Acceso seguro</span>
          </div>

          {/* ── Header ── */}
          <h2 className="font-display font-bold text-3xl tracking-[-0.02em] text-brand-silver leading-[1.05]">
            Iniciar sesión
          </h2>
          <p className="mt-2.5 text-sm text-brand-silver-dim leading-relaxed">
            Ingrese sus credenciales para acceder a su espacio operativo.
          </p>

          {/* ── Error ── */}
          {error && (
            <div
              role="alert"
              className="mt-7 p-4 rounded-brand bg-danger-soft border border-danger-DEFAULT/30 flex items-start gap-3 animate-scaleIn"
            >
              <Shield size={16} className="text-danger-DEFAULT mt-0.5 flex-shrink-0" />
              <p className="text-sm text-danger-DEFAULT font-medium">{error}</p>
            </div>
          )}

          {/* ── Form ── */}
          <form onSubmit={handleLogin} className="mt-7 space-y-5">

            {/* Usuario */}
            <div>
              <label className="flex items-center gap-2 mb-2.5">
                <span className="eyebrow text-brand-silver-dim">Usuario o documento</span>
              </label>
              <div className="relative group">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-silver-mute group-focus-within:text-brand-indigo transition-colors duration-300" />
                <input
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-11 pr-4 py-3.5 rounded-brand bg-brand-navy/40 border border-white/[0.06] text-brand-silver placeholder:text-brand-silver-mute/60 text-[15px] focus:ring-2 focus:ring-brand-indigo/40 focus:border-brand-indigo/50 focus:bg-brand-navy/60 outline-none transition-all duration-300 ease-brand"
                  placeholder="nickname · email · cédula"
                  autoComplete="username"
                />
                <div className="absolute inset-0 rounded-brand pointer-events-none opacity-0 group-focus-within:opacity-100 transition-opacity duration-500"
                     style={{ boxShadow: '0 0 30px -4px rgba(79,125,255,0.35)' }} />
              </div>
            </div>

            {/* Contraseña */}
            <div>
              <label className="flex items-center justify-between mb-2.5">
                <span className="eyebrow text-brand-silver-dim">Contraseña</span>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-[10px] tracking-[0.18em] font-semibold text-brand-indigo-soft hover:text-brand-indigo uppercase transition-colors"
                >
                  ¿Olvidó?
                </button>
              </label>
              <div className="relative group">
                <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-silver-mute group-focus-within:text-brand-indigo transition-colors duration-300" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 pr-12 py-3.5 rounded-brand bg-brand-navy/40 border border-white/[0.06] text-brand-silver placeholder:text-brand-silver-mute/60 text-[15px] focus:ring-2 focus:ring-brand-indigo/40 focus:border-brand-indigo/50 focus:bg-brand-navy/60 outline-none transition-all duration-300 ease-brand"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-silver-mute hover:text-brand-silver transition-colors"
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full overflow-hidden rounded-brand py-4 font-semibold text-[15px] tracking-wide text-white transition-all duration-300 ease-brand active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed shadow-brand-lift"
              style={{
                background: 'linear-gradient(135deg, #4F7DFF 0%, #2B4EB8 100%)',
              }}
            >
              {/* Sheen overlay */}
              <span
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 50%)',
                }}
                aria-hidden="true"
              />

              <span className="relative flex items-center justify-center gap-2.5">
                {loading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    <span>Autenticando…</span>
                  </>
                ) : (
                  <>
                    <span>Acceder al sistema</span>
                    <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                  </>
                )}
              </span>
            </button>
          </form>

          {/* ── Footer legal ── */}
          <div className="mt-10 pt-6 border-t border-white/[0.06]">
            <p className="text-[11px] text-center text-brand-silver-mute leading-relaxed">
              Acceso exclusivo para personal autorizado de Legalplus Partners S.A.S.
              <br />
              Al ingresar acepta los términos de uso y política de privacidad.
            </p>
          </div>

          <div className="mt-5 flex items-center justify-center gap-2 text-[10px] tracking-[0.26em] font-semibold text-brand-silver-mute/70 uppercase">
            <Lock size={10} className="text-brand-indigo" />
            Conexión cifrada TLS 1.3
            <span className="text-brand-indigo">·</span>
            <span className="flex items-center gap-1">
              <span className="w-1 h-1 rounded-full bg-ok-DEFAULT animate-pulse" />
              Online
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
